import React, { useState } from 'react';
import { Step } from 'react-joyride';
import { useHistory } from 'react-router-dom';

import AppBar from 'components/AppBar';
import EmptyList from 'components/EmptyList';
import FloatingButton from 'components/FloatingButton';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Item from 'components/List/StyledListItem';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Search from 'components/Search';
import Tour from 'components/Tour';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedListModel } from 'models/groupedList';
import { htmlT } from 'utils/generic';

import { List, Container } from './styles';

export interface Props {
  isLoading: boolean;
  ruleList: GroupedListModel[];
  tourHasAccess?: boolean;
  tourLoading?: boolean;
  onSelect?: (value: any) => void;
}

const RulesList = ({ isLoading, ruleList, tourHasAccess, tourLoading }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const [showSearch, setShowSearch] = useState(false);
  const [rulesResult, setRulesResult] = useState<GroupedListModel[]>([]);

  const handleItemClick = (rule: GroupedListModel) => history.push(`/corporate/rule/${rule.id}`);

  const handleOnSearch = (value: string) => {
    if (value) {
      setRulesResult(
        ruleList.filter((name: GroupedListModel) => name.name && name.name.toLowerCase().includes(value.toLowerCase())),
      );
    } else setRulesResult([]);
  };

  const initialSteps: Step[] = [
    {
      target: '#rules-add-button',
      content: t('rulesComponent.addButtonTour'),
      disableBeacon: true,
      placement: 'center',
    },
  ];

  const renderList = () => (
    <>
      <Search
        show={ruleList.length >= 10}
        onShowSearch={(isSearch: boolean) => setShowSearch(isSearch)}
        onSearch={handleOnSearch}
      />

      <Mobile>
        {showSearch ? (
          <List>
            <GroupedItem
              lineGroup
              quantityText={htmlT(t, 'groupList.searchResults', { count: rulesResult?.length })}
              isSearch={showSearch}
              items={rulesResult}
              selectableType="arrow"
              onSelection={handleItemClick}
            />
          </List>
        ) : (
          <>
            <GroupedList
              lineGroup
              isAlphabetic
              isSelectable={false}
              items={ruleList}
              isSearch={showSearch}
              selectableType="arrow"
              onSelection={handleItemClick}
            />
          </>
        )}
      </Mobile>

      <Desktop>
        <List>
          {(showSearch ? rulesResult : ruleList).map((rule) => (
            <Item
              key={`rule-${rule.id}`}
              title={`${rule.name}`}
              onClick={() => handleItemClick(rule)}
              badgesContent={rule.badges}
              unGroup
            />
          ))}
        </List>
      </Desktop>
    </>
  );
  const renderEmptyView = () => (
    <div>
      <EmptyList
        grey
        title={t('rulesComponent.emptyTitle')}
        description={t('rulesComponent.emptyDescription')}
        image="/corporate/images/bookmark-note-colored-48.svg"
        backgroundImage="/corporate/images/group-3.svg"
      />
    </div>
  );
  return (
    <>
      <Mobile>
        <AppBar goBack={() => history.push('/corporate/dashboard')}>
          <h5>{t('rulesComponent.listHeader')}</h5>
        </AppBar>
      </Mobile>
      {isLoading ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader title={t('userMenu.rulesTitle')} subtitle={t('userMenu.rulesDetail')} />
          </Desktop>

          <Container hasList={ruleList.length > 0}>
            <Tour
              type="cancelable"
              show={!tourLoading && !tourHasAccess}
              hideArrow
              steps={initialSteps}
              disableOverlayClose
              disableScrollParentFix
            />
            {ruleList && ruleList.length ? renderList() : renderEmptyView()}
          </Container>
        </>
      )}
      <FloatingButton zIndex={2} onClick={() => history.push('/corporate/rule')} className="rules-add-button" />
    </>
  );
};

export default RulesList;
